import React, { useState, useMemo, useEffect } from "react";
import { SectionCard } from "../../../Component/RetailerMainComponents/SectionCard";
import { CheckCircle2, Clock, FileDown, Layers, Plus, XCircle } from "lucide-react";
import { Badge } from "../../../Component/RetailerMainComponents/Badge";
import { useLocation, useNavigate } from "react-router-dom";
import Pagination from "../../../Component/Common/Pagination";
import RefreshButton from "../../../Component/Common/RefreshButton";
import BlueButton from "../../../Component/Common/BlueButton";
import PhoneVerifyModal from "./CreateCustomer/PhoneVerifyModal";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { BASE_URL } from "../../../config/api";
import SimpleTable from "../../../Component/Common/SimpleTable";
import SoldWarrantyCard from "../../../Component/Common/SoldWarrantyCard";
import ExpandableCard from "../../../Component/Common/ExpandableCard";
import ApproveRejectModal from "../Admin/WarrantiesClaimsView/ApproveRejectModal";
import { formatDateTimeSplit } from "../../../Component/Common/formatDateTime";
import ClaimCard from "../../../Component/Common/ClaimCard";


export default function WarrantyView() {
  const Navigate = useNavigate();
  const location = useLocation();
  const [showVerify, setShowVerify] = useState(false);
  const [activeTab, setActiveTab] = useState("sold"); 
  const [soldWarranties, setSoldWarranties] = useState([]);
  const [soldLoading, setSoldLoading] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);

  const [soldPage, setSoldPage] = useState(1);
  const [soldTotalPages, setSoldTotalPages] = useState(1);

  const [claimsPage, setClaimsPage] = useState(1);
  const [claimsTotalPages, setClaimsTotalPages] = useState(1);

  const [warrantyStatus, setWarrantyStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const SEARCH_MIN_LENGTH = 4;
  const SEARCH_DEBOUNCE = 500; // ms

  const cameFromKpi = location.state?.fromKpi || false;
  const cameFromKpi1 = location.state?.fromKpi1 || false;
  const clickedType = location.state?.type || "";
  const fromPendingKpi = location.state?.fromPendingKpi || false;
  const fromWarrantySubmitted = location.state?.fromWarrantySubmitted || false;

  const authUserString = localStorage.getItem("authUserData");
  const authUser = authUserString ? JSON.parse(authUserString) : null;
  const retailerId = authUser?.id; // adjust if key name differs

  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  // const [claimsPage, setClaimsPage] = useState(1);
  // const [claimsTotalPages, setClaimsTotalPages] = useState(1);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isRetailerMobile = isMobile;

  // ---------------------- EXISTING MOCK DATA ----------------------
  const fetchSoldWarranties = async (
  page = 1,
  status = warrantyStatus,
  search = searchTerm
) => {
  page = Number(page) || 1;

  try {
    setSoldLoading(true);

    const url = new URL(`${BASE_URL}warranty/get-devices`);
    url.searchParams.append("retailer_id", retailerId);
    url.searchParams.append("page", page);

    if (status !== "all") {
      url.searchParams.append("is_approved", status);
    }

    if (search && search.length >= 4) {
      url.searchParams.append("search", search);
    }

    const res = await fetch(url.toString());
    const json = await res.json();

    if (!res.ok) {
      throw new Error(json?.message || "Failed to fetch sold warranties");
    }

    const formatted = (json.data || []).map((device) => {
      // ✅ Parse invoice_json PER DEVICE
      let invoiceNumber = "-";

      if (device.invoice_json) {
        try {
          const parsedInvoice = JSON.parse(device.invoice_json);
          invoiceNumber = parsedInvoice?.invoice_number || "-";
        } catch (e) {
          invoiceNumber = "-";
        }
      }

      return {
        RawID: device.id,
        id: device.w_code,

        // ✅ Correct invoice number
        saleInvoice: invoiceNumber,

        productName: device.product_name || "-",
        warrantyType: device.category_name || "-",
        soldOn: formatDateTimeSplit(device.created_at),
        amount: device.product_mrp || 0,

        status:
          device.is_approved === 1
            ? "Active"
            : device.is_approved === 2
            ? "Rejected"
            : "Provisioning",


        is_approved: device.is_approved,

        rejectReason: device.status_remark || device.reject_remark || "-",
        registeredBy: device.customer?.name || "-",
        registeredByCode: device.customer?.c_code || "-",
      };
    });

    setSoldWarranties(formatted);
    setSoldPage(json.current_page);
    setSoldTotalPages(json.last_page);

  } catch (err) {
    console.error("Fetch sold warranties failed:", err);
  } finally {
    setSoldLoading(false);
  }
};

// useEffect(() => {
//   if (!retailerId) return;
//   fetchSoldWarranties(soldPage);
// }, [retailerId, soldPage]);

useEffect(() => {
  const timer = setTimeout(() => {
    if (searchInput.length >= 4) {
      setSearchTerm(searchInput);
      setSoldPage(1);
      fetchSoldWarranties(1, warrantyStatus, searchInput);
    }

    // if user clears input → reset search
    if (searchInput.length === 0) {
      setSearchTerm("");
      setSoldPage(1);
      fetchSoldWarranties(1, warrantyStatus, "");
    }
  }, SEARCH_DEBOUNCE);

  return () => clearTimeout(timer);
}, [searchInput]);


useEffect(() => {
  if (!retailerId) return;
  fetchSoldWarranties(1, warrantyStatus);
  setSoldPage(1);
}, [retailerId, warrantyStatus]);


  const fetchWarrantyClaims = async (page = 1) => {
  try {
    setClaimsLoading(true);

    const res = await fetch(
      `${BASE_URL}warranty/claims/list?retailer_id=${retailerId}&page=${page}`
    );

    const json = await res.json();

    if (!res.ok || !json.status) {
      throw new Error("Failed to fetch claims");
    }

    const apiData = json.data;

    const formatted = (apiData.data || []).map((c) => ({
      ...c, // keep full object

      ui: {
        id: c.claim_code,
        saleInvoice: c.device?.id || "-",
        productName: c.device?.product_name || "-",
        imei: c.device?.imei1 || "-",
        // claimDate: c.created_at?.slice(0, 10) || "-",
        claimDate: formatDateTimeSplit(c.created_at),
        claim_type:c.claim_type === "pickup" ? "Pickup" : c.claim_type === "drop" ? "Drop" : null,
        status: c.status,
        reasons: c.coverages?.map(x => x.coverage_title) || [],
        registeredBy: c.customer?.name || "-",
      },
    }));

    setClaims(formatted);

    // ✅ BACKEND PAGINATION META
    setClaimsPage(apiData.current_page);
    setClaimsTotalPages(apiData.last_page);

  } catch (err) {
    console.error("Fetch claims failed:", err);
  } finally {
    setClaimsLoading(false);
  }
};



  useEffect(() => {
    if (activeTab === "claims" && retailerId) {
      fetchWarrantyClaims(claimsPage);
    } 
  }, [activeTab, retailerId, claimsPage]);

  const approvedOnly = soldWarranties.filter(w => w.status === "Approved");

  const finalSoldList = cameFromKpi1 && clickedType === "totalSales"
  ? approvedOnly
  : soldWarranties;


  const soldPaginated = finalSoldList;


  // ---------------------- EXISTING FILTER STATE ----------------------
  const [warrantyFilter, setWarrantyFilter] = useState("all");

  const filteredWarrantyClaims = useMemo(() => {
    if (fromWarrantySubmitted) {
      return claims.filter((w) => w.status === "Pending");
    }

    if (warrantyFilter === "all") return claims;
    return claims.filter((w) => w.status === warrantyFilter);
  }, [claims, warrantyFilter, fromWarrantySubmitted]);

  // ---------------------- NEW STATE FOR OUTSTANDING TAB ----------------------
  const [activeOutstandingTab, setActiveOutstandingTab] = useState(
    fromPendingKpi ? "noninvoiced" : "invoiced"
  );

  // const invoicedOutstanding = soldWarranties.map((w) => ({
  //   id: w.id,
  //   productName: w.productName,
  //   warrantyType: w.warrantyType,
  //   soldOn: w.soldOn,
  //   amount: w.amount,
  //   paymentStatus: w.status === "Approved" ? "Paid" : "Unpaid",
  // }));

  const nonInvoicedOutstanding = [
    {
      id: "NIW-3001",
      productName: "Soundbox Pro",
      warrantyType: "6 Month Device Protection",
      soldOn: "2025-09-20",
      amount: 1200,
    },
    {
      id: "NIW-3002",
      productName: "POS Terminal Basic",
      warrantyType: "1 Year Extended Warranty",
      soldOn: "2025-11-02",
      amount: 1500,
    },
  ];

  // ==== Pagination State for Sold Warranties ====


// ==== Pagination State for Warranty Claims ====


// const claimPerPage = 10;
// const claimTotalPages = Math.ceil(filteredWarrantyClaims.length / claimPerPage);
// const claimPaginated = filteredWarrantyClaims.slice(
//   (claimPage - 1) * claimPerPage,
//   claimPage * claimPerPage
// );

// ==== Pagination for Outstanding (dynamic) ====
// const currentOutstandingData =
//   activeOutstandingTab === "invoiced"
//     ? invoicedOutstanding
//     : nonInvoicedOutstanding;

// const [outPage, setOutPage] = useState(1);
// const outPerPage = 2;
// const outTotalPages = Math.ceil(currentOutstandingData.length / outPerPage);
// const outPaginated = currentOutstandingData.slice(
//   (outPage - 1) * outPerPage,
//   outPage * outPerPage
// );

const isFromSidebar =
  !cameFromKpi && !cameFromKpi1 && !fromPendingKpi;

  const approvalMap = {
    0: { label: "Provisioning", variant: "warning" },
    1: { label: "Active", variant: "success" },
    2: { label: "Rejected", variant: "danger" },
  };

  const claimStatusMap = {
    pending: { variant: "warning" },
    Approved: { variant: "success" },
    Rejected: { variant: "danger" },
  };

  const claimTypeMap = {
    Pickup: { variant: "success" }, // green
    Drop: { variant: "blue" },      // blue
  };

// const soldRows = useMemo(() => {
//   return soldPaginated.map((sw) => {
//     const approval = approvalMap[sw.is_approved] || {
//       label: "Unknown",
//       variant: "gray",
//     };

//     return {
//       raw: sw,
//       cells: [
//         sw.id,
//         sw.saleInvoice,
//         sw.productName,
//         sw.soldOn,

//         <Badge variant={approval.variant}>
//           {approval.label}
//         </Badge>,

//         ...(!cameFromKpi1
//           ? [
//               sw.rejectReason || "-",
//               sw.registeredBy || "-",
//             ]
//           : []),
//       ],
//     };
//   });
// }, [soldPaginated, cameFromKpi1]);

const soldRows = useMemo(() => {
  return soldPaginated.map((sw) => {
    const approval = approvalMap[sw.is_approved] || {
      label: "Unknown",
      variant: "gray",
    };

    return {
      raw: sw,
      cells: [
        {
          title: sw.id,
          sub: sw.saleInvoice,
        },
          // sw.saleInvoice,
        {
          title: sw.productName,
          sub: sw.warrantyType,
          // image: (
          //   <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
          //     {sw.productName?.[0]}
          //   </div>
          // ),
        },
        {
          title: sw.soldOn.date,
          sub: sw.soldOn.time,
        },
        <Badge variant={approval.variant}>
          {approval.label}
        </Badge>,
        ...(!cameFromKpi1
          ? [
              sw.rejectReason || "-",
              {
                title: sw.registeredBy,
                sub: sw.registeredByCode,
                // image: `https://ui-avatars.com/api/?name=${sw.registeredBy}&background=edf2ff&color=1e40af`,
              },
            ]
          : []),
      ],
    };
  });
}, [soldPaginated, cameFromKpi1]);


  const claimRows = useMemo(() => {
  return filteredWarrantyClaims.map((w) => {
    const color =
      w.ui.status === "Active"
        ? "green"
        : w.ui.status === "Rejected"
        ? "red"
        : "yellow";

      const approval = claimStatusMap[w.ui.status] || { variant: "gray" };

    return {
      raw: w, // ✅ FULL CLAIM OBJECT PASSED
      cells: [
        w.ui.id,
        // w.ui.saleInvoice,
        w.ui.productName,
        w.ui.imei,
        // w.ui.claimDate,
        
        {
          title: w.ui.claimDate.date,
          sub: w.ui.claimDate.time,
        },
      <Badge variant={claimTypeMap[w.ui.claim_type]?.variant || "gray"}>
          {w.ui.claim_type}
        </Badge>,
        <Badge variant={approval.variant}>
          {w.ui.status}
        </Badge>,
        ...(!fromWarrantySubmitted
          ? [<div className="max-h-16 overflow-y-auto space-y-1 text-left pr-1">
            {w.ui.reasons.length === 0 ? (
              <span className="text-slate-400">—</span>
            ) : (
              w.ui.reasons.map((r, i) => (
                <div key={i} className="text-xs text-slate-700">
                  • {r}
                </div>
              ))
            )}
          </div>
          , w.ui.registeredBy]
                    : []),
                ],
              };
            });
          }, [filteredWarrantyClaims, fromWarrantySubmitted]);



  // ---------------------- UI ----------------------
  return (
    <div className="space-y-4">

      {/* ================= TABS ================= */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setActiveTab("sold")}
        className={`px-4 py-2 text-sm rounded-xl font-medium transition-all duration-300
          ${
            activeTab === "sold"
              ? "bg-blue-600 backdrop-blur-xl border border-white/40 text-white shadow-md"
              : "bg-white/10 backdrop-blur-xl border border-white/20 text-slate-800 hover:bg-white/20"
          }
        `}
        >
          Sold Warranties
        </button>

       <button
        onClick={() => setActiveTab("claims")}
        className={`px-4 py-2 text-sm rounded-xl font-medium transition-all duration-300
          ${
            activeTab === "claims"
              ? "bg-blue-600 backdrop-blur-xl border border-white/40 text-white shadow-md"
              : "bg-white/10 backdrop-blur-xl border border-white/20 text-slate-800 hover:bg-white/20"
          }
        `}
      >
        Warranty Claims
      </button>
      </div>

      {/* ================= EXISTING SOLD WARRANTY TABLE ================= */}
      {activeTab === "sold" &&
      (isFromSidebar || cameFromKpi1 ) && !fromWarrantySubmitted && (
        <SectionCard
          title="Sold Warranties"
          isRetailerMobile={isRetailerMobile}
          className="md:bg-white bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
          right={
            <div className="block md:hidden">
              <BlueButton
                onClick={() => Navigate("/customer-create")}
                className="flex items-center gap-2"
              >
                <Plus size={14} />
                Add Warranty
              </BlueButton>
            </div>
          }
        >

          <div className="flex flex-col gap-3 mb-3 
                sm:flex-col 
                md:flex-row md:items-center md:justify-end">

            <input
              type="text"
              placeholder="Search warranty / customer / product / category"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-70 text-xs rounded-xl px-3 py-2
              bg-white/10 backdrop-blur-xl border border-white/30
              text-slate-900 placeholder-slate-600
              focus:outline-none focus:ring-2 focus:ring-blue-400/40"            />
              {/* <button className="hidden md:inline-flex text-[11px] px-2.5 py-1.5 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 items-center gap-1">
                <FileDown className="w-3 h-3" />
                <span>Download sold warranty</span>
              </button> */}

            <div className="flex gap-2">
              <div className="flex gap-2 items-center">
                {[
                  { value: "all", icon: Layers, title: "All", activeClass: "bg-blue-600 text-white border-blue-600" },
                  { value: 1, icon: CheckCircle2, title: "Active", activeClass: "bg-emerald-600 text-white border-emerald-600" },
                  { value: 0, icon: Clock, title: "Provisioning", activeClass: "bg-amber-500 text-white border-amber-500" },
                  { value: 2, icon: XCircle, title: "Rejected", activeClass: "bg-rose-600 text-white border-rose-600" },
                ].map(({ value, icon: Icon, title, activeClass }) => {
                  const isActive = warrantyStatus === value;

                  return (
                    <button
                      key={title}
                      onClick={() => setWarrantyStatus(value)}
                      title={title}
                      className={`p-2 rounded-full border transition-all duration-300
                        flex items-center gap-1 text-xs font-medium
                        ${
                          isActive
                            ? activeClass
                            : isRetailerMobile
                            ? "bg-white/10 backdrop-blur-xl border-white/30 text-slate-900 hover:bg-white/20"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{title}</span>
                    </button>
                  );
                })}
              </div>

              <div className="hidden md:block">
                <BlueButton
                onClick={() => Navigate("/customer-create")}
                className="flex items-center gap-2"
              >
                <Plus size={14} />

                {/* Hide text on small screens */}
                <span className="hidden sm:inline">
                  Add Customer
                </span>
              </BlueButton>
              </div>

              <div className="hidden md:block"><RefreshButton onClick={() => fetchSoldWarranties(soldPage, warrantyStatus)} /></div>
            </div>

            </div>
          {soldLoading ? (
            <div className="py-8 flex justify-center">
              <RegularLoader />
            </div>
          ) : soldRows.length === 0 ? (
            <div className="py-10 text-center text-sm font-semibold text-slate-400">
              No sold warranties found
            </div>
          ) : (
            <>
              {/* DESKTOP */}
              <div className="hidden md:block">
                <SimpleTable
                  headers={[
                    "Warranty ID",
                    // "Invoice",
                    "Product",
                    // "Warranty Type",
                    "Sold On",
                    // "Amount",
                    "Status",
                    ...(!cameFromKpi1 ? ["Reject Reason", "Customer"] : []),
                  ]}
                  rows={soldRows}
                  currentPage={soldPage}
                  totalPages={soldTotalPages}
                  onPageChange={(page) => {
                    setSoldPage(page);
                    fetchSoldWarranties(page, warrantyStatus, searchTerm);
                  }}

                  showSrNo
                  renderActions={(row) => (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        Navigate(`/warranty-details/${row.RawID}`);
                      }}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      View
                    </button>
                  )}
                />
              </div>

              {/* MOBILE */}
              <div className="block md:hidden space-y-3">
                {soldPaginated.map((sw) => (
                  <SoldWarrantyCard
                    key={sw.id}
                    data={sw}
                    onView={() => Navigate(`/warranty-details/${sw.RawID}`)}
                  />
                ))}

                {soldTotalPages > 1 && (
                  <Pagination
                    currentPage={soldPage}
                    totalPages={soldTotalPages}
                  onPageChange={(page) => {
                    setSoldPage(page);
                    fetchSoldWarranties(page, warrantyStatus, searchTerm);
                  }}
                  />
                )}
              </div>
            </>
          )}

        </SectionCard>
      )}

      {/* ================= EXISTING WARRANTY CLAIMS ================= */}
      {activeTab === "claims" && (
        <SectionCard
          title="Warranty Claims"
          isRetailerMobile={isRetailerMobile}
          className="md:bg-white bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
          right={
            !fromWarrantySubmitted && (
              <div className="flex gap-2 items-center">
              <select
                value={warrantyFilter}
                onChange={(e) => setWarrantyFilter(e.target.value)}
                className={`
                  text-xs px-3 py-2 rounded-xl outline-none transition-all duration-300
                  appearance-none
                  ${
                    isRetailerMobile
                      ? "bg-white/10 backdrop-blur-2xl border border-white/30 text-slate-900 shadow-[0_10px_25px_rgba(31,38,135,0.25)] focus:ring-2 focus:ring-blue-400/40"
                      : "border border-slate-300 bg-slate-50 text-slate-700"
                  }
                `}
              >
                <option value="all">All</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Pending">Pending</option>
              </select>
              

              <div className="hidden md:block">
                <RefreshButton onClick={fetchWarrantyClaims} />
              </div>
            </div>
            )
          }
        >
          {/* ================= CONTENT ================= */}
          {claimsLoading ? (
            /* ---------- LOADER ---------- */
            <div className="py-8 flex justify-center">
              <RegularLoader />
            </div>
          ) : claimRows.length === 0 ? (
            /* ---------- EMPTY STATE ---------- */
            <p className="py-6 text-center text-slate-400 text-sm">
              No claims found
            </p>
          ) : (
            <>
              {/* ================= DESKTOP TABLE ================= */}
              <div className="hidden md:block overflow-x-auto">
                <SimpleTable
                  headers={[
                    "Claim ID",
                    // "Invoice",
                    "Product",
                    "IMEI",
                    "Claim Date",
                    "Claim Type",
                    "Status",
                    ...(!fromWarrantySubmitted ? ["Reason", "Customer"] : []),
                  ]}
                  rows={claimRows}
                  currentPage={claimsPage}
                  totalPages={claimsTotalPages}
                  onPageChange={setClaimsPage}
                  showSrNo
                  renderActions={(row) => (
                    <button
                      onClick={() => {
                        setSelectedClaim(row);
                        setShowClaimModal(true);
                      }}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      View
                    </button>
                  )}
                />

              </div>

              {/* ================= MOBILE / TABLET CARDS ================= */}
              {/* <div className="block md:hidden space-y-3">
                {filteredWarrantyClaims.map((w) => {
                  const badgeColor =
                    w.status === "active"
                      ? "green"
                      : w.status === "Rejected"
                      ? "red"
                      : "yellow";

                  return (
                    <ExpandableCard
                      key={w.id}
                      title={w.ui.productName}
                      badge={{
                        label: w.ui.status,
                        color:
                          w.ui.status === "Approved"
                            ? "green"
                            : w.ui.status === "Rejected"
                            ? "red"
                            : "yellow",
                      }}
                      primaryFields={[
                        { label: "Claim ID", value: w.ui.id },
                        { label: "Invoice", value: w.ui.saleInvoice },
                        { label: "IMEI", value: w.ui.imei },
                        { label: "Claim Date", value: w.ui.claimDate.date },
                      ]}
                      extraFields={
                        fromWarrantySubmitted
                          ? []
                          : [
                              {
                                label: "Reasons",
                                value: (
                                  <div className="max-h-16 overflow-y-auto pr-1">
                                    {w.ui.reasons.map((r, i) => (
                                      <div key={i}>• {r}</div>
                                    ))}
                                  </div>
                                ),
                              },
                              {
                                label: "Customer",
                                value: w.ui.registeredBy,
                              },
                            ]
                      }
                      onView={() => {
                        setSelectedClaim(w);
                        setShowClaimModal(true);
                      }}
                    />


                  );
                })}
              </div> */}

              <div className="block md:hidden space-y-3">
                {filteredWarrantyClaims.map((w) => (
                  <ClaimCard
                    key={w.id}
                    data={w}
                    hideExtra={fromWarrantySubmitted}
                    onView={() => {
                      setSelectedClaim(w);
                      setShowClaimModal(true);
                    }}
                    isRetailerMobile={isRetailerMobile}
                  />
                ))}
                {claimsTotalPages > 1 && (
                <div className="mt-3">
                  <Pagination
                    currentPage={claimsPage}
                    totalPages={claimsTotalPages}
                    onPageChange={setClaimsPage}
                  />
                </div>
              )}
              </div>

              {/* ================= PAGINATION ================= */}
              {/* {claimsTotalPages > 1 && (
                <div className="mt-3">
                  <Pagination
                    currentPage={claimsPage}
                    totalPages={claimsTotalPages}
                    onPageChange={setClaimsPage}
                  />
                </div>
              )} */}
            </>
          )}
        </SectionCard>
      )}

      {/* ================= NEW OUTSTANDING SECTION ================= */}
      
      {showClaimModal && selectedClaim && (
        <ApproveRejectModal
          claim={selectedClaim}
          isAdmin={false}      // 🔒 retailer view
          isCompany={false}   // 🔒 retailer view
          onClose={() => {
            setShowClaimModal(false);
            setSelectedClaim(null);
          }}
          onAssign={() => {}}
          onApprove={() => {}}
          onReject={() => {}}
        />
      )}

      {/* {showVerify && (
        <PhoneVerifyModal
          onClose={() => setShowVerify(false)}
          onResult={(success, mobile, customerData) => {
            setShowVerify(false);

            Navigate("/customer-create", {
              state: {
                customer: customerData || null,
                verifiedMobile: success ? mobile : null, // ✅ ALWAYS PASS MOBILE
              },
            });
          }}
        />
      )} */}
    </div>          
  );
}


// import react from "react";
// import { SectionCard } from "../../../Component/RetailerMainComponents/SectionCard";
// import { FileDown } from "lucide-react";
// import { Badge } from "../../../Component/RetailerMainComponents/Badge";

// export default function WarrantyView({ soldWarranties, warrantyClaims, warrantyFilter, setWarrantyFilter }) {
//   return (
//     <div className="space-y-4">
//       {/* Sold warranty (extended warranty at time of sale) */}
//       <SectionCard
//         title="Sold Warranties"
//         right={
//           <button className="hidden md:inline-flex text-[11px] px-2.5 py-1.5 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 items-center gap-1">
//             <FileDown className="w-3 h-3" />
//             <span>Download sold warranty</span>
//           </button>
//         }
//       >
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b border-slate-100 text-slate-500">
//                 <th className="py-2 text-left">Warranty ID</th>
//                 <th className="py-2 text-left">Invoice</th>
//                 <th className="py-2 text-left">Product</th>
//                 <th className="py-2 text-left">Warranty Type</th>
//                 <th className="py-2 text-left">Sold On</th>
//                 <th className="py-2 text-right">Amount</th>
//                 <th className="py-2 text-left">Status</th>
//                 <th className="py-2 text-left">Reject Reason</th>
//                 <th className="py-2 text-left">Registered By</th>
//               </tr>
//             </thead>
//             <tbody>
//               {soldWarranties.map((sw) => {
//                 const soldBadgeColor =
//                   sw.status === "Approved"
//                     ? "green"
//                     : sw.status === "Rejected"
//                     ? "red"
//                     : "yellow";
//                 return (
//                   <tr
//                     key={sw.id}
//                     className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                   >
//                     <td className="py-1.5 text-xs text-slate-700 font-medium">
//                       {sw.id}
//                     </td>
//                     <td className="py-1.5 text-xs text-slate-700">{sw.saleInvoice}</td>
//                     <td className="py-1.5 text-xs text-slate-700">{sw.productName}</td>
//                     <td className="py-1.5 text-xs text-slate-700">{sw.warrantyType}</td>
//                     <td className="py-1.5 text-xs text-slate-700">{sw.soldOn}</td>
//                     <td className="py-1.5 text-xs text-right text-blue-700">
//                       ₹{sw.amount.toLocaleString()}
//                     </td>
//                     <td className="py-1.5 text-xs">
//                       <Badge color={soldBadgeColor}>{sw.status}</Badge>
//                     </td>
//                     <td className="py-1.5 text-[11px] text-slate-700">
//                       {sw.rejectReason || "-"}
//                     </td>
//                     <td className="py-1.5 text-[11px] text-slate-700">
//                       {sw.registeredBy || "-"}
//                     </td>
//                   </tr>
//                 );
//               })}
//               {soldWarranties.length === 0 && (
//                 <tr>
//                   <td
//                     colSpan={9}
//                     className="py-4 text-center text-xs text-slate-500"
//                   >
//                     No sold warranties found.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </SectionCard>

//       {/* Warranty Claims */}
//       <SectionCard
//         title="Warranty Claims"
//         right={
//           <div className="flex items-center gap-2">
//             <select
//               value={warrantyFilter}
//               onChange={(e) => setWarrantyFilter(e.target.value)}
//               className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-slate-50"
//             >
//               <option value="all">All status</option>
//               <option value="Approved">Approved</option>
//               <option value="Rejected">Rejected</option>
//               <option value="Pending">Pending</option>
//             </select>
//             <button className="hidden md:inline-flex text-[11px] px-2.5 py-1.5 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 items-center gap-1">
//               <FileDown className="w-3 h-3" />
//               <span>Download claims</span>
//             </button>
//           </div>
//         }
//       >
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b border-slate-100 text-slate-500">
//                 <th className="py-2 text-left">Claim ID</th>
//                 <th className="py-2 text-left">Invoice</th>
//                 <th className="py-2 text-left">Product</th>
//                 <th className="py-2 text-left">IMEI / Serial</th>
//                 <th className="py-2 text-left">Status</th>
//                 <th className="py-2 text-left">Raised By</th>
//                 <th className="py-2 text-left">Registered By</th>
//                 <th className="py-2 text-left">Reason</th>
//                 <th className="py-2 text-left">Reject Reason</th>
//                 <th className="py-2 text-left">Settlement</th>
//                 <th className="py-2 text-right">Approved Amt.</th>
//               </tr>
//             </thead>
//             <tbody>
//               {warrantyClaims.map((w) => {
//                 const statusBadgeColor =
//                   w.status === "Approved"
//                     ? "green"
//                     : w.status === "Rejected"
//                     ? "red"
//                     : "yellow";
//                 const settlementBadgeColor =
//                   w.settlementStatus === "Settled"
//                     ? "green"
//                     : w.settlementStatus === "In Progress"
//                     ? "blue"
//                     : "gray";

//                 return (
//                   <tr
//                     key={w.id}
//                     className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                   >
//                     <td className="py-1.5 text-xs text-slate-700">{w.id}</td>
//                     <td className="py-1.5 text-xs text-slate-700">
//                       <div className="font-medium">{w.saleInvoice}</div>
//                       <div className="text-[10px] text-slate-500">
//                         Claim: {w.claimDate}
//                       </div>
//                     </td>
//                     <td className="py-1.5 text-xs text-slate-700">{w.productName}</td>
//                     <td className="py-1.5 text-xs text-slate-700">{w.imei}</td>
//                     <td className="py-1.5 text-xs">
//                       <Badge color={statusBadgeColor}>{w.status}</Badge>
//                     </td>
//                     <td className="py-1.5 text-xs text-slate-700">
//                       {w.raisedBy || "-"}
//                     </td>
//                     <td className="py-1.5 text-xs text-slate-700">
//                       {w.registeredBy || "-"}
//                     </td>
//                     <td className="py-1.5 text-[11px] text-slate-700">{w.reason}</td>
//                     <td className="py-1.5 text-[11px] text-slate-700">
//                       {w.rejectReason || "-"}
//                     </td>
//                     <td className="py-1.5 text-xs">
//                       <Badge color={settlementBadgeColor}>
//                         {w.settlementStatus || "Not started"}
//                       </Badge>
//                     </td>
//                     <td className="py-1.5 text-xs text-right text-blue-700">
//                       ₹{w.approvedAmount.toLocaleString()}
//                     </td>
//                   </tr>
//                 );
//               })}
//               {warrantyClaims.length === 0 && (
//                 <tr>
//                   <td
//                     colSpan={11}
//                     className="py-4 text-center text-xs text-slate-500"
//                   >
//                     No warranty claims available.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </SectionCard>

//       <SectionCard title="Warranty Notes">
//         <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
//           <li>
//             Sold warranties are approved or rejected by admin. Common reject
//             reasons: payment not paid, wrong product info, or incorrect price.
//           </li>
//           <li>
//             Warranty claims show both admin status (approved / rejected /
//             pending) and settlement status (settled / in progress).
//           </li>
//           <li>
//             Claims can be raised either by retailer or by customer – this is
//             shown under "Raised By" and the exact user who registered is shown
//             under "Registered By".
//           </li>
//         </ul>
//       </SectionCard>
//     </div>
//   );
// }